// src/marketing/budget/aggregate.ts
// Agrégats purs sur les dépenses quotidiennes. `today` est toujours passé en
// paramètre (YYYY-MM-DD) : aucune horloge interne, tout est testable.
import type { DailyExpense } from '../../lib/types'

export function sumBy(
  expenses: DailyExpense[],
  key: 'category' | 'platform',
): { key: string; total: number }[] {
  const totals = new Map<string, number>()
  for (const e of expenses) {
    const k = (key === 'category' ? e.category : e.platform) ?? '—'
    totals.set(k, (totals.get(k) ?? 0) + e.amount)
  }
  return [...totals.entries()]
    .map(([k, total]) => ({ key: k, total }))
    .sort((a, b) => b.total - a.total)
}

export function actualByCategory(expenses: DailyExpense[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const e of expenses) {
    totals.set(e.category, (totals.get(e.category) ?? 0) + e.amount)
  }
  return totals
}

function toIso(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function parseIso(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`)
}

function addDays(iso: string, days: number): string {
  const d = parseIso(iso)
  d.setUTCDate(d.getUTCDate() + days)
  return toIso(d)
}

/** Lundi de la semaine du jour donné (clé de regroupement hebdo). */
function mondayOf(iso: string): string {
  const d = parseIso(iso)
  const shift = (d.getUTCDay() + 6) % 7 // 0=lun … 6=dim
  d.setUTCDate(d.getUTCDate() - shift)
  return toIso(d)
}

function firstOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

export function expenseSeries(
  expenses: DailyExpense[],
  granularity: 'day' | 'week' | 'month',
  today: string,
): { date: string; value: number }[] {
  const keys: string[] = []
  if (granularity === 'day') {
    for (let i = 29; i >= 0; i--) keys.push(addDays(today, -i))
  } else if (granularity === 'week') {
    const monday = mondayOf(today)
    for (let i = 11; i >= 0; i--) keys.push(addDays(monday, -7 * i))
  } else {
    const first = parseIso(firstOfMonth(today))
    for (let i = 11; i >= 0; i--) {
      const d = new Date(first)
      d.setUTCMonth(d.getUTCMonth() - i)
      keys.push(toIso(d))
    }
  }

  const totals = new Map<string, number>(keys.map((k) => [k, 0]))
  for (const e of expenses) {
    const k =
      granularity === 'day' ? e.expenseDate
      : granularity === 'week' ? mondayOf(e.expenseDate)
      : firstOfMonth(e.expenseDate)
    if (totals.has(k)) totals.set(k, (totals.get(k) ?? 0) + e.amount)
  }
  return keys.map((k) => ({ date: k, value: totals.get(k) ?? 0 }))
}

export function periodTotals(
  expenses: DailyExpense[],
  today: string,
): { today: number; last7: number; monthToDate: number } {
  const sevenDaysAgo = addDays(today, -6)
  const monthStart = firstOfMonth(today)
  let todayTotal = 0
  let last7 = 0
  let monthToDate = 0
  for (const e of expenses) {
    if (e.expenseDate === today) todayTotal += e.amount
    if (e.expenseDate >= sevenDaysAgo && e.expenseDate <= today) last7 += e.amount
    if (e.expenseDate >= monthStart && e.expenseDate <= today) monthToDate += e.amount
  }
  return { today: todayTotal, last7, monthToDate }
}
